
import React, { useState } from 'react';
import { Shield, Lock, Users, Bell, Globe, FileText, Trash2, Download, Save } from 'lucide-react';
import { MOCK_ACTIVITY_LOGS, MOCK_ORG_SETTINGS } from '../../constants';
import { OrgSettings } from '../../types';

export const SettingsView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'security' | 'compliance' | 'team' | 'notifications'>('general');
  const [settings, setSettings] = useState<OrgSettings>(MOCK_ORG_SETTINGS);
  const [newDomain, setNewDomain] = useState('');

  const handleAddDomain = () => {
    if (newDomain && !settings.allowedDomains.includes(newDomain)) {
        setSettings({...settings, allowedDomains: [...settings.allowedDomains, newDomain]});
        setNewDomain('');
    }
  };

  const removeDomain = (domain: string) => {
      setSettings({...settings, allowedDomains: settings.allowedDomains.filter(d => d !== domain)});
  };

  const renderTabContent = () => {
      switch(activeTab) {
          case 'general':
              return (
                  <div className="space-y-6">
                      <div className="bg-white p-6 rounded-lg border border-slate-200">
                          <h3 className="text-lg font-medium text-slate-900 mb-4">Organization Profile</h3>
                          <div className="grid grid-cols-1 gap-4">
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Organization Name</label>
                                  <input type="text" defaultValue="BioReg Global Strategies" className="w-full p-2 border border-slate-300 rounded-md" disabled />
                              </div>
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Public Profile Visibility</label>
                                  <select 
                                    value={settings.visibility}
                                    onChange={e => setSettings({...settings, visibility: e.target.value as any})}
                                    className="w-full p-2 border border-slate-300 rounded-md"
                                  >
                                      <option value="public">Public (Visible to everyone)</option>
                                      <option value="verified_only">Verified Members Only</option>
                                      <option value="private">Private (Hidden)</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Default NDA URL</label>
                                  <input 
                                    type="url" 
                                    value={settings.defaultNDALink}
                                    onChange={e => setSettings({...settings, defaultNDALink: e.target.value})}
                                    className="w-full p-2 border border-slate-300 rounded-md"
                                    placeholder="https://..."
                                  />
                                  <p className="text-xs text-slate-500 mt-1">This NDA will be suggested when replying to RFQs.</p>
                              </div>
                          </div>
                      </div>
                  </div>
              );
          case 'security':
              return (
                  <div className="space-y-6">
                      <div className="bg-white p-6 rounded-lg border border-slate-200">
                          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2"><Lock className="w-5 h-5"/> Access Control</h3>
                          <div className="flex items-center justify-between mb-6">
                              <div>
                                  <h4 className="font-medium text-slate-900">Require 2FA for all members</h4>
                                  <p className="text-sm text-slate-500">Enforce two-factor authentication for everyone in your organization.</p>
                              </div>
                              <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
                                  <input 
                                    type="checkbox" 
                                    checked={settings.require2FA}
                                    onChange={() => setSettings({...settings, require2FA: !settings.require2FA})}
                                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                                  />
                                  <label className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${settings.require2FA ? 'bg-green-400' : 'bg-gray-300'}`}></label>
                              </div>
                          </div>

                          <h4 className="font-medium text-slate-900 mb-2">Allowed Email Domains</h4>
                          <div className="flex gap-2 mb-3">
                              <input 
                                type="text" 
                                placeholder="example.com" 
                                value={newDomain}
                                onChange={e => setNewDomain(e.target.value)}
                                className="flex-grow p-2 border border-slate-300 rounded-md text-sm"
                              />
                              <button onClick={handleAddDomain} className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm">Add</button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                              {settings.allowedDomains.map(d => (
                                  <span key={d} className="inline-flex items-center bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                                      {d} <button onClick={() => removeDomain(d)} className="ml-2 text-slate-400 hover:text-red-600"><Trash2 className="w-3 h-3"/></button>
                                  </span>
                              ))}
                          </div>
                      </div>
                  </div>
              );
          case 'compliance':
              return (
                  <div className="space-y-6">
                      <div className="bg-white p-6 rounded-lg border border-slate-200">
                          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2"><Shield className="w-5 h-5"/> GDPR & Data Privacy</h3>
                          <div className="flex gap-4">
                              <button className="flex items-center px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50">
                                  <Download className="w-4 h-4 mr-2"/> Export Data
                              </button>
                              <button className="flex items-center px-4 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50">
                                  <Trash2 className="w-4 h-4 mr-2"/> Request Deletion
                              </button>
                          </div>
                      </div>
                      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                          <div className="p-4 border-b border-slate-200">
                              <h3 className="font-medium text-slate-900">Activity Log</h3>
                          </div>
                          <table className="min-w-full divide-y divide-slate-200">
                              <thead className="bg-slate-50">
                                  <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">IP</th>
                                  </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-slate-200">
                                  {MOCK_ACTIVITY_LOGS.map(log => (
                                      <tr key={log.id}>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{log.action}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{log.user}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{log.timestamp}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{log.ipAddress}</td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      </div>
                  </div>
              );
          case 'notifications':
              return (
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                      <h3 className="text-lg font-medium text-slate-900 mb-4">Email Preferences</h3>
                      <div className="space-y-4">
                          <div className="flex items-center justify-between">
                              <div>
                                  <h4 className="text-sm font-medium text-slate-900">RFQ Updates</h4>
                                  <p className="text-xs text-slate-500">Receive emails when you get a proposal or message.</p>
                              </div>
                              <input type="checkbox" checked={settings.notifications.emailRfqUpdates} readOnly className="h-4 w-4 text-blue-600 rounded"/>
                          </div>
                          <div className="flex items-center justify-between">
                              <div>
                                  <h4 className="text-sm font-medium text-slate-900">Security Alerts</h4>
                                  <p className="text-xs text-slate-500">Login attempts and password changes.</p>
                              </div>
                              <input type="checkbox" checked={settings.notifications.emailSecurityAlerts} readOnly className="h-4 w-4 text-blue-600 rounded"/>
                          </div>
                          <div className="flex items-center justify-between">
                              <div>
                                  <h4 className="text-sm font-medium text-slate-900">Marketing & Tips</h4>
                                  <p className="text-xs text-slate-500">Product updates and newsletters.</p>
                              </div>
                              <input type="checkbox" checked={settings.notifications.emailMarketing} readOnly className="h-4 w-4 text-blue-600 rounded"/>
                          </div>
                      </div>
                  </div>
              );
          default:
              return null;
      }
  };

  return (
    <div className="max-w-4xl mx-auto">
        <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900">Organization Settings</h1>
            <p className="text-slate-500">Manage your company profile, security, and compliance preferences.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-1">
                <nav className="space-y-1">
                    <button 
                        onClick={() => setActiveTab('general')}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'general' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Globe className="w-4 h-4 mr-2"/> General
                    </button>
                    <button 
                        onClick={() => setActiveTab('security')}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'security' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Lock className="w-4 h-4 mr-2"/> Security
                    </button>
                    <button 
                        onClick={() => setActiveTab('compliance')}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'compliance' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Shield className="w-4 h-4 mr-2"/> Compliance
                    </button>
                    <button 
                        onClick={() => setActiveTab('notifications')}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'notifications' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Bell className="w-4 h-4 mr-2"/> Notifications
                    </button>
                </nav>
                
                <div className="mt-8 pt-8 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-semibold">Legal</p>
                    <div className="space-y-2 text-sm">
                        <a href="#" className="block text-blue-600 hover:underline">Privacy Policy</a>
                        <a href="#" className="block text-blue-600 hover:underline">Terms of Service</a>
                        <a href="#" className="block text-blue-600 hover:underline">Data Processing Agreement</a>
                    </div>
                </div>
            </div>

            <div className="md:col-span-3">
                {renderTabContent()}
            </div>
        </div>
    </div>
  );
};
